import React, { useCallback, useEffect, useRef } from 'react';

import { isLocalStorageSupported } from '../../common/utils';
import { useRemoteActionsOnInterval } from '../../controllers/use-remote-actions-on-interval';
import {
	RemoteActionsContainer,
	useRemoteActionLastUpdatedAt,
	useRemoteActionsData,
} from '../../services/remote-actions-store';

import {
	type LifecycleEventHandler,
	type RemoteActionsInternalProps,
	type RemoteActionsProps,
} from './types';

export type { LifecycleEventHandler } from './types';

type EmitAnalyticsOnNewActionsProcessedProps = {
	onLifecycleEvent: LifecycleEventHandler;
};

const EmitAnalyticsOnNewActionsProcessed = ({
	onLifecycleEvent,
}: EmitAnalyticsOnNewActionsProcessedProps) => {
	const lastUpdatedAt = useRemoteActionLastUpdatedAt();
	const remoteActionsData = useRemoteActionsData();

	useEffect(() => {
		if (lastUpdatedAt > 0) {
			onLifecycleEvent({
				action: 'pendingActions',
				attributes: {
					lastUpdatedAt,
					hasPendingActions: Object.keys(remoteActionsData).length > 0,
					pendingActionsIds: Object.keys(remoteActionsData),
				},
			});
		}
	}, [lastUpdatedAt, remoteActionsData, onLifecycleEvent]);

	return null;
};

const RemoteActionsManagerInternal = ({
	endpoint,
	product,
	productData,
	pollingInterval,
	idleTime,
	onLifecycleEvent,
}: RemoteActionsInternalProps) => {
	const onPollingError = useCallback(() => {
		if (onLifecycleEvent) {
			onLifecycleEvent({ action: 'pollingError' });
		}
	}, [onLifecycleEvent]);
	useRemoteActionsOnInterval({
		endpoint,
		product,
		productData,
		pollingInterval,
		idleTime,
		onPollingError,
	});

	return onLifecycleEvent ? (
		<EmitAnalyticsOnNewActionsProcessed onLifecycleEvent={onLifecycleEvent} />
	) : null;
};

const RemoteActionsManager = (props: RemoteActionsProps): React.JSX.Element | null => {
	const { product, onLifecycleEvent } = props;
	const isLocalStorageSupportedRef = useRef(isLocalStorageSupported());

	useEffect(() => {
		if (!isLocalStorageSupportedRef.current) {
			if (onLifecycleEvent) {
				onLifecycleEvent({
					action: 'disabled',
					attributes: { reason: 'LOCALSTORAGE_UNSUPPORTED' },
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!isLocalStorageSupportedRef.current) {
		return null;
	}

	return (
		<RemoteActionsContainer product={product} isGlobal>
			<RemoteActionsManagerInternal {...props} />
		</RemoteActionsContainer>
	);
};

export default RemoteActionsManager;
